import { ConsentPlatform, ConsentResponseService } from '@csp/csp-common-consent';
import { Maybe } from '@csp/csp-common-model';
import { ConsentType } from '@csp/dmdp-api-user-dto';
import { ScheduledConsentActionStatusTypeV1 } from '@csp/dto';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLoading } from '../../../../common/hooks/useLoading';
import { getUserSelector } from '../../../security/ducks/principalContextSelectors';
import { ConsentFormMapper } from '../../mapper/ConsentFormMapper';
import { ConsentForm } from '../../model/ConsentForm';
import { GenericConsentTypes } from '../../model/GenericConsentType';
import { mapUserRolesToVideoTrainingConsentTypes } from '../../util/mapUserRolesToVideoTrainingConsentTypes';
import { ConsentContextState } from '../model/ConsentContextState';
import { ConsentFormState } from '../model/ConsentFormState';

const emptyConsentFormState: ConsentFormState = {
  currentConsents: [],
  previousConsents: [],
};

export const useConsentContextProvider = (): ConsentContextState => {
  const { data = emptyConsentFormState, error, isLoading, setLoading, clearLoading } = useLoading<ConsentFormState>();
  const user = useSelector(getUserSelector);

  const fetchConsents = useCallback(async () => {
    if (user && !isLoading) {
      setLoading();

      try {
        const consentTypes = [...GenericConsentTypes, ...mapUserRolesToVideoTrainingConsentTypes(user.roles)];

        const currentConsents = await ConsentResponseService.getScheduledConsents(
          { skippedConsents: [] },
          user,
          ConsentPlatform.PORTAL,
          consentTypes,
        );
        const previousConsents = ConsentResponseService.getUnresolvedConsentsFromScheduledConsents(currentConsents);
        clearLoading({
          data: {
            currentConsents: currentConsents.map(ConsentFormMapper.from),
            previousConsents: previousConsents.map(ConsentFormMapper.from),
          },
        });
      } catch (error) {
        clearLoading({ error });
      }
    }
  }, [clearLoading, isLoading, setLoading, user]);

  const getConsent = useCallback(
    (type: ConsentType): Maybe<ConsentForm> => {
      const { currentConsents } = data;

      return currentConsents.find(consent => consent.consentType === type);
    },
    [data],
  );

  const hasTypesWithStatus = useCallback(
    (types: ConsentType[], status: ScheduledConsentActionStatusTypeV1): boolean => {
      const { currentConsents } = data;

      return currentConsents.some(consent => types.includes(consent.consentType) && consent.approvalStatus === status);
    },
    [data],
  );

  const hasPendingConsents = useCallback(
    (types: ConsentType[]): boolean => hasTypesWithStatus(types, ScheduledConsentActionStatusTypeV1.PENDING),
    [hasTypesWithStatus],
  );

  const hasApprovedConsents = useCallback(
    (types: ConsentType[]): boolean => hasTypesWithStatus(types, ScheduledConsentActionStatusTypeV1.APPROVED),
    [hasTypesWithStatus],
  );

  const hasPendingVideoTrainingConsents = useCallback((): boolean => {
    const consentTypes = mapUserRolesToVideoTrainingConsentTypes(user?.roles ?? []);

    return hasPendingConsents(consentTypes);
  }, [hasPendingConsents, user]);

  const hasOutdatedConsents = useCallback(
    (types: ConsentType[]): boolean => {
      const { previousConsents } = data;

      return previousConsents.some(
        ({ consentType }) => types.includes(consentType) && hasPendingConsents([consentType]),
      );
    },
    [data, hasPendingConsents],
  );

  const hasOutdatedVideoTrainingConsents = useCallback((): boolean => {
    const consentTypes = mapUserRolesToVideoTrainingConsentTypes(user?.roles ?? []);

    return hasOutdatedConsents(consentTypes);
  }, [hasOutdatedConsents, user]);

  const firstPendingConsent = useCallback(
    (types: ConsentType[]): Maybe<ConsentForm> => {
      const { currentConsents } = data;

      return currentConsents.find(
        consent =>
          types.includes(consent.consentType) && consent.approvalStatus === ScheduledConsentActionStatusTypeV1.PENDING,
      );
    },
    [data],
  );

  const resetConsents = useCallback(() => {
    clearLoading({ data: emptyConsentFormState });
  }, [clearLoading]);

  return useMemo(
    () => ({
      consents: data.currentConsents,
      error,
      fetchConsents,
      firstPendingConsent,
      getConsent,
      hasOutdatedConsents,
      hasOutdatedVideoTrainingConsents,
      hasPendingConsents,
      hasPendingVideoTrainingConsents,
      hasApprovedConsents,
      isLoading,
      resetConsents,
    }),
    [
      data,
      error,
      fetchConsents,
      firstPendingConsent,
      getConsent,
      hasOutdatedConsents,
      hasOutdatedVideoTrainingConsents,
      hasPendingConsents,
      hasPendingVideoTrainingConsents,
      hasApprovedConsents,
      isLoading,
      resetConsents,
    ],
  );
};
